import { ComponentType, Suspense } from "react";
import { ContentfulEntry, EntryRef } from "frontend/contentful/schema/sys";
import { ErrorBoundary } from "design-system/components/system/error-boundary/error-boundary";
import { contentfulComponents } from "frontend/contentful/contentful-components";
import { useContentful } from "frontend/hooks/use-contentful";
import {
  ArchivePageEntry,
  PageTemplates,
} from "frontend/contentful/schema/pages";
import { isContentType } from "frontend/contentful/lib/is-content-type";
import {
  ContentfulComponentRenderProps,
  ContentfulFactoryComponent,
} from "frontend/contentful/lib/create-contentful-component";
import { usePageProps } from "frontend/hooks/use-page-props";
import { AudienceBlocksWrapperEntry } from "frontend/contentful/schema/blocks";

interface RenderContentfulEntryProps {
  entry?: EntryRef<ContentfulEntry> | null;
  options?: Record<string, unknown>;
}

const archiveTemplates: Record<
  PageTemplates,
  ContentfulFactoryComponent<ArchivePageEntry>
> = {
  "ExEd Program Finder": contentfulComponents.exedProgramFinderArchivePage,
  "Person Profiles": contentfulComponents.archivePage,
  "HBSO Course Finder": contentfulComponents.hbsoCourseFinderArchivePage,
  "Alumni Directory Landing":
    contentfulComponents.alumniDirectoryLandingArchivePage,
  "Alumni Directory Search":
    contentfulComponents.alumniDirectorySearchArchivePage,
  "Alumni Class Notes": contentfulComponents.alumniClassNotesDetailpage,
  "Alumni Profiles": contentfulComponents.alumniProfilesDetailPage,
  "Alumni Magazine": contentfulComponents.archivePage,
};

function renderBlocks(
  blocks: Array<EntryRef<ContentfulEntry> | null | undefined>,
  options?: Record<string, unknown>,
) {
  return (
    <>
      {blocks.filter(Boolean).map((block, index) => (
        <RenderContentfulEntry
          key={block?.sys?.id ?? index}
          entry={block}
          options={options}
        />
      ))}
    </>
  );
}

function parsePermissionRoles(
  permissions: Array<{
    fields?: {
      roleCode?: string | null;
    };
  }>,
): Array<string> {
  return permissions.flatMap((permission) => {
    const roleCode = permission.fields?.roleCode?.trim();

    if (!roleCode) return [];

    return roleCode.includes(",")
      ? roleCode
          .split(",")
          .map((code) => code.trim())
          .filter(Boolean)
      : [roleCode];
  });
}

export function RenderContentfulEntry({
  entry,
  options,
}: RenderContentfulEntryProps) {
  const { getEntry, getEntries } = useContentful();
  const pageProps = usePageProps();

  if (!entry) return null;

  const resolvedEntry = getEntry(entry);

  let Component: // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ComponentType<ContentfulComponentRenderProps<any, any>> | undefined;

  const isAudienceBlockWrapper = isContentType<AudienceBlocksWrapperEntry>(
    "audienceBlocksWrapper",
    resolvedEntry,
  );

  if (
    isContentType<ArchivePageEntry>("archivePage", resolvedEntry) &&
    resolvedEntry.fields.template
  ) {
    Component = archiveTemplates[resolvedEntry.fields.template];
  } else if (isAudienceBlockWrapper) {
    const audienceEntry = resolvedEntry as AudienceBlocksWrapperEntry;
    const blocks = audienceEntry.fields?.blocks ?? [];

    const permissionRefs = audienceEntry.fields?.permissions ?? [];
    const permissions = getEntries(permissionRefs) ?? [];

    //remove const permissions = getEntries(audienceEntry.fields?.permissions) ?? [];
    const permissionRoles = parsePermissionRoles(permissions);

    const userRoles = pageProps.session?.user.roles ?? [];
    const hasPermission =
      userRoles.length > 0 &&
      userRoles.some((role) => permissionRoles.includes(role));

    const isPublicBlock = permissionRoles.length === 0;
    const hasUnresolvedPermissions =
      permissionRefs.length > 0 && permissions.length === 0;

    if (hasUnresolvedPermissions) {
      console.warn(
        "AudienceBlocksWrapper entry has unresolved permissions. Please check the entry's permissions and ensure they are published.",
      );
      return null;
    }

    if (isPublicBlock && userRoles.length === 0) {
      return renderBlocks(blocks);
    }

    if (hasPermission) {
      return renderBlocks(blocks);
    }

    return null;
  } else {
    const componentType = resolvedEntry?.sys?.contentType?.sys?.id;
    Component = componentType
      ? contentfulComponents[componentType as keyof typeof contentfulComponents]
      : undefined;
  }

  if (!Component) return null;

  return (
    <ErrorBoundary>
      <Suspense fallback={null}>
        <Component entry={resolvedEntry} options={options} />
      </Suspense>
    </ErrorBoundary>
  );
}
